import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserDashboardService } from '../services/user-dashboard.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AppMaterialModule } from '../app-modules/app-material.module';
import { UtilModule } from '../app-modules/util.module';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { USER_DASHBOARD_TUTORIAL_FLOW, USER_WORKFLOW_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';
import { TutorialVideoService } from '../services/tutorial-video.service';
import { NotificationService } from '../services/notification.service';

const PENDING_RAES_REVIEW = 'Pending RAES Review' as const;
const PENDING_RRE_REVIEW = 'Pending RRE Review' as const;
const IN_PROGRESS = 'In Progress' as const;
const COMPLETED = 'Completed' as const;
const UNASSIGNED = 'Unassigned' as const;
const OVERDUE = 'Overdue' as const;

const USER_TAB = {
  DASHBOARD: 'Dashboard',
  MANAGE_APPROVAL: 'Manage Approval',
  MANAGE_PROJECT: 'Manage Project',
  HELP_SUPPORT: 'Help & Support',
} as const;

type UserTab = typeof USER_TAB[keyof typeof USER_TAB];
type ProjectStatus =
  | 'Unassigned'
  | 'Assigned'
  | 'Submitted'
  | 'In Progress'
  | 'Pending RRE Review'
  | 'Pending RAES Review'
  | 'Completed'
  | 'Deleted'
  | 'Overdue';

interface TeamMember {
  id: string;
  name: string;
  role: 'Member' | 'Manager';
}

interface ProjectRecord {
  id: number;
  name: string;
  collection: string;
  subCollections: string[];
  contactPerson: string;
  departmentId: string;
  dueDate: string;
  status: ProjectStatus;
  assignedTo?: string;
  managerId?: string;
  adminDocuments: string[];
  uploadedDocuments: string[];
  createdAt?: string;
  updatedAt?: string;
}

const QUICK_NAV_CARD_KEY = {
  ALL: 'all',
  OVERDUE: 'overdue',
  ASSIGNED: 'assigned',
  UNASSIGNED: 'unassigned',
} as const;

type QuickNavCardKey = typeof QUICK_NAV_CARD_KEY[keyof typeof QUICK_NAV_CARD_KEY];

const QUICK_FILTER = {
  ALL: 'all',
  OVERDUE: 'overdue',
  PENDING_ADMIN_REVIEW: 'pending-admin-review',
  PENDING_MANAGER_APPROVAL: 'pending-manager-approval',
  IN_PROGRESS: 'in-progress',
  UNASSIGNED: 'unassigned',
  COMPLETED: 'completed',
} as const;

type QuickFilter = typeof QUICK_FILTER[keyof typeof QUICK_FILTER];

type DueState = 'normal' | 'warning' | 'near' | 'overdue';

interface QuickNavCard {
  key: QuickNavCardKey;
  label: string;
  value: number;
  icon: string;
  color: string;
}

interface HelpResourceItem {
  title: string;
  description: string;
  icon: string;
  buttonLabel: string;
  metaItems: { icon: string; label: string }[];
  screenId?: string;
  resourceType?: 'document' | 'video';
  action: () => void;
}

@Component({
  selector: 'app-user-dashboard',
  standalone: true,
  imports: [AppMaterialModule, UtilModule, TutorialVideoWidgetComponent],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.css'
})
export class UserDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly userDashboardTutorialSequence: TutorialVideoStep[] = USER_DASHBOARD_TUTORIAL_FLOW;
  readonly userWorkflowTutorialSequence: TutorialVideoStep[] = USER_WORKFLOW_TUTORIAL_FLOW;
  userName = '';
  userId = 'u1';
  profileImagePath = 'assets/profile-placeholder.svg';
  userTabs: UserTab[] = [USER_TAB.DASHBOARD, USER_TAB.MANAGE_PROJECT, USER_TAB.MANAGE_APPROVAL, USER_TAB.HELP_SUPPORT];
  activeTab: UserTab = USER_TAB.DASHBOARD;
  helpActiveTab: 'home' | 'help' | 'contact' = 'home';
  isSideNavCollapsed = false;
  quickFilter: QuickFilter = QUICK_FILTER.ALL;
  isLoading = true;
  dashboardSearchText = '';
  completedSectionCollapsed = true;
  openingVideoScreenId = '';
  private queryParamsSubscription?: Subscription;

  filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'unassigned', label: UNASSIGNED },
    { value: QUICK_FILTER.IN_PROGRESS, label: IN_PROGRESS },
    { value: 'pending-rre-review', label: PENDING_RRE_REVIEW },
    { value: 'pending-raes-review', label: PENDING_RAES_REVIEW },
    { value: 'completed', label: COMPLETED },
    { value: 'overdue', label: OVERDUE }
  ];

  filterDateOptions = [
    { label: 'All Records', value: '0' },
    { label: 'Last 7 Days', value: '7' },
    { label: 'Last 30 Days', value: '30' },
    { label: 'Last 60 Days', value: '60' },
    { label: 'Last 3 Months', value: '90' },
    { label: 'Last 6 Months', value: '180' },
  ];
  selectedDateFilter = '0';
  selectedStatusFilter = 'all';

  displayedColumns: string[] = ['projectName', 'collection', 'subCollection', 'contactPerson', 'createdDate', 'dueDate', 'status'];
  dataSource = new MatTableDataSource<ProjectRecord>([]);
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  searchText = '';
  pageCount = 0;
  pages: (number | string)[] = [];

  departmentMembers: TeamMember[] = [
    { id: 'u1', name: 'You', role: 'Member' },
    { id: 'u2', name: 'Priya Sharma', role: 'Member' },
    { id: 'u3', name: 'David Miller', role: 'Member' },
    { id: 'm1', name: 'Emma Wilson', role: 'Manager' }
  ];

  projects: ProjectRecord[] = [];

  readonly documentResources: HelpResourceItem[] = [
    {
      title: 'User manual',
      description: 'Step-by-step guide to navigating the application.',
      icon: 'menu_book',
      buttonLabel: 'Download User Manual',
      metaItems: [
        { icon: 'slideshow', label: 'PPTX' },
        { icon: 'auto_stories', label: '48 pages' }
      ],
      resourceType: 'document',
      action: () => this.downloadUserManual()
    },
    {
      title: 'Process flow',
      description: 'See how request moves from submission to approval.',
      icon: 'account_tree',
      buttonLabel: 'Download Process Flow',
      metaItems: [
        { icon: 'picture_as_pdf', label: 'PDF' },
        { icon: 'auto_stories', label: '1 page' }
      ],
      resourceType: 'document',
      action: () => this.downloadProcessFlow()
    }
  ];

  readonly videoResources: HelpResourceItem[] = [
    {
      title: 'Dashboard overview',
      description: 'Overview of the User Dashboard and Request Management',
      icon: 'dashboard',
      buttonLabel: 'Watch Video',
      metaItems: [
        { icon: 'timer', label: '3:50 mins' }
      ],
      screenId: 'user-dashboard',
      resourceType: 'video',
      action: () => this.openTutorialVideo('user-dashboard')
    },
    {
      title: 'Project detail screen',
      description: 'Review project details, status, and document activity in one place.',
      icon: 'fact_check',
      buttonLabel: 'Watch Video',
      metaItems: [
        { icon: 'timer', label: '2:30 mins' }
      ],
      screenId: 'user-detailed-screen',
      resourceType: 'video',
      action: () => this.openTutorialVideo('user-detailed-screen')
    },
    {
      title: 'Manage project',
      description: 'Review all requests and check their current status',
      icon: 'folder_open',
      buttonLabel: 'Watch Video',
      metaItems: [
        { icon: 'timer', label: '1:31 mins' }
      ],
      screenId: 'manage-project-user-dashboard',
      resourceType: 'video',
      action: () => this.openTutorialVideo('manage-project-user-dashboard')
    },
    {
      title: 'Manage approval',
      description: 'Review and Manage RRE Approval Requests',
      icon: 'fact_check',
      buttonLabel: 'Watch Video',
      metaItems: [
        { icon: 'timer', label: '3:18 mins' }
      ],
      screenId: 'manage-approvals',
      resourceType: 'video',
      action: () => this.openTutorialVideo('manage-approvals')
    }
  ];

  readonly helpResources: HelpResourceItem[] = [
    ...this.documentResources,
    ...this.videoResources,
  ];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userDashboardService: UserDashboardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly tutorialVideoService: TutorialVideoService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.getUserDashboardData();
    try {
      const stored = sessionStorage.getItem('UserInfo');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.userName) {
          this.userName = parsed.userName;
        }
      }
    } catch (e) {
      console.info('Failed to read UserInfo from sessionStorage', e);
    }

    const me = this.departmentMembers.find(member => member.id === this.userId);
    if (me && this.userName === '') {
      this.userName = me.name;
    }

    // Ensure the correct tab is active based on the current URL
    const currentUrl = this.router.url || '';
    if (currentUrl.includes('/user-dashboard/manage-approval')) {
      this.activeTab = USER_TAB.MANAGE_APPROVAL;
    } else {
      this.activeTab = USER_TAB.DASHBOARD;
    }

    // Allow external entry points (e.g. the header help icon) to open Help & Support
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      if (params['tab'] === 'help') {
        this.activeTab = USER_TAB.HELP_SUPPORT;
        this.cdr.detectChanges();
      }
    });
  }

  ngAfterViewInit() {
    this.initializePaginator();
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
  }

  private initializePaginator(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.page.subscribe(() => this.updatePagination());
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  updatePagination(): void {
    if (!this.paginator) {return};

    const length = this.dataSource.filteredData?.length || this.dataSource.data.length;
    this.paginator.length = length;
    this.pageCount = Math.ceil(length / this.paginator.pageSize);
    this.pages = this.generatePaginationPages();
  }

  private generatePaginationPages(): (number | string)[] {
    const MAX_PAGES = 10;
    const currentPage = (this.paginator?.pageIndex || 0) + 1;
    const pages: (number | string)[] = [];

    if (this.pageCount <= MAX_PAGES) {
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    }

    pages.push(1);

    const rangeSize = 2;
    const startPage = Math.max(2, currentPage - rangeSize);
    const endPage = Math.min(this.pageCount - 1, currentPage + rangeSize);

    if (startPage > 2) {
      pages.push('...');
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < this.pageCount - 1) {
      pages.push('...');
    }

    pages.push(this.pageCount);

    return pages;
  }

  reloadPage(): void {
    globalThis.location.reload();
  }

  downloadUserManual(): void {
    this.downloadAsset('assets/CERT_User_manual_V1.0.pptx', 'CERT_User_manual_V1.0.pptx');
  }

  downloadProcessFlow(): void {
    this.downloadAsset('assets/CERT_Process_Flow.pdf', 'CERT_Process_Flow.pdf');
  }

  openHelpResource(resource: HelpResourceItem): void {
    resource.action();
  }

  isOpeningVideo(screenId: string): boolean {
    return this.openingVideoScreenId === screenId;
  }

  private openTutorialVideo(screenId: string): void {
    if (this.openingVideoScreenId) {
      return;
    }

    const popup = window.open('about:blank', '_blank');
    if (popup) {
      popup.document.write('<title>Opening video walkthrough...</title><p style="font-family: Arial, sans-serif; padding: 16px;">Opening video walkthrough...</p>');
      popup.document.close();

      try {
        popup.opener = null;
      } catch {
        // Ignore browsers that do not allow assigning opener.
      }
    }

    this.openingVideoScreenId = screenId;

    this.tutorialVideoService.getTutorialVideoUrl(screenId).subscribe({
      next: (resource) => {
        this.openingVideoScreenId = '';

        if (!resource.signedUrl) {
          popup?.close();
          this.notificationService.showError('Tutorial video is not available right now.');
          return;
        }

        if (popup && !popup.closed) {
          popup.location.replace(resource.signedUrl);
        } else {
          window.open(resource.signedUrl, '_blank', 'noopener,noreferrer');
        }
      },
      error: () => {
        this.openingVideoScreenId = '';
        popup?.close();
        this.notificationService.showError('Unable to open the tutorial video. Please try again.');
      }
    });
  }

  private downloadAsset(assetPath: string, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = assetPath;
    anchor.download = fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
  
  goToPage(page: number): void {
    if (!this.paginator) {return};
    this.paginator.pageIndex = page;
    this.paginator._changePageSize(this.paginator.pageSize);
  }

  displayPageNumbers(): number[] {
    const MAX_DISPLAY = 5;
    const currentPage = (this.paginator?.pageIndex || 0) + 1;
    const pages: number[] = [];

    if (this.pageCount <= MAX_DISPLAY) {
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    }

    const rangeSize = 2;
    const startPage = Math.max(1, currentPage - rangeSize);
    const endPage = Math.min(this.pageCount, currentPage + rangeSize);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  getStartIndex(): number {
    if (!this.paginator) {return 0;}
    return (this.paginator.pageIndex * this.paginator.pageSize) + 1;
  }

  getEndIndex(): number {
    if (!this.paginator){ return 0};
    const end = (this.paginator.pageIndex + 1) * this.paginator.pageSize;
    return Math.min(end, this.paginator.length);
  }

  onPageSizeChange(pageSize: any): void {
    if (!this.paginator) {return};
    this.paginator.pageSize = Number.parseInt(pageSize, 10);
    this.paginator.firstPage();
    this.dataSource._updateChangeSubscription();
    this.cdr.detectChanges();
    this.updatePagination();
  }

  getUserDashboardData(){
    this.isLoading = true;
    this.userDashboardService.getUserDashboardData().subscribe({
      next:(response:any)=>{
        console.info("Dashboard data fetched successfully", response);
        const projectsData = response.payload;
        if (projectsData && Array.isArray(projectsData)) {
          this.projects = projectsData.map((item: any) => this.mapApiResponseToProjectRecord(item));
          this.dataSource.data = projectsData;
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          this.cdr.detectChanges()
          setTimeout(() => {
            this.initializePaginator();
            this.updatePagination();
          });
        }
        this.isLoading = false;
      },
      error:(error:any)=>{
        console.trace("Error fetching dashboard data", error);
        this.isLoading = false;
      }
    });
  }

  private mapApiResponseToProjectRecord(apiResponse: any): ProjectRecord {
    const departmentStatus = this.mapStatusToProjectStatus(apiResponse.sub_task_status || apiResponse.status);
    const assignedTo = apiResponse.assigned_person_name ? apiResponse.assigned_person_name : apiResponse.assigned_person_email_id;
    
    const uploadedFiles = this.extractFileNames(apiResponse.uploaded_files);
    
    return {
      id: apiResponse.project_details_id,
      name: apiResponse.project_name,
      collection: apiResponse.collection_name,
      subCollections: apiResponse.sub_collection_name || [],
      contactPerson: apiResponse.contact_person || '',
      departmentId: apiResponse.department_id || '',
      dueDate: apiResponse.due_date,
      status: departmentStatus,
      assignedTo: assignedTo,
      managerId: undefined,
      adminDocuments: uploadedFiles,
      uploadedDocuments: uploadedFiles,
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at
    };
  }

  private extractFileNames(uploadedFiles: any[]): string[] {
    if (!uploadedFiles) {
      return [];
    }
    if (!Array.isArray(uploadedFiles)) {
      return [];
    }
    return uploadedFiles.map(file => file.file_name).filter(Boolean);
  }

  private mapStatusToProjectStatus(apiStatus: string): ProjectStatus {
    const statusMap: { [key: string]: ProjectStatus } = {
      'submitted': 'Submitted',
      'assigned': 'Assigned',
      'in progress': IN_PROGRESS,
      'pending rre review': PENDING_RRE_REVIEW,
      'pending raes review': PENDING_RAES_REVIEW,
      'completed': COMPLETED,
      'unassigned': UNASSIGNED,
      'deleted': 'Deleted',
      'overdue': OVERDUE
    };
    
    // Convert to lowercase for case-insensitive matching
    const normalizedStatus = apiStatus?.toLowerCase() || 'unassigned';
    return statusMap[normalizedStatus] || apiStatus as ProjectStatus;
  }
  get dashboardStats(): QuickNavCard[] {
    return [
      { key: 'all', label: 'All Projects', value: this.projects.length, icon: 'apps', color: '#1E88E5' },
      { key: 'overdue', label: 'Overdue Projects', value: this.overdueProjectsCount, icon: 'schedule', color: '#D32F2F' },
      { key: 'assigned', label: 'Assigned Projects', value: this.assignedProjectsCount, icon: 'assignment_ind', color: '#FB8C00' },
      { key: 'unassigned', label: 'Unassigned Projects', value: this.unassignedProjectsCount, icon: 'person_off', color: '#6B7280' }
    ];
  }

  get displayUserName(): string {
    return this.userName?.trim() ? this.userName : 'Prabhagaran Arumugam (TMNA)';
  }

  get filteredProjects(): ProjectRecord[] {
    let results: ProjectRecord[];
    switch (this.quickFilter) {
      case 'overdue':
        results = this.projects.filter(project => this.isOverdue(project) && project.status !== 'Deleted');
        break;
      case 'pending-admin-review':
        results = this.projects.filter(project => project.status === PENDING_RAES_REVIEW);
        break;
      case 'pending-manager-approval':
        results = this.projects.filter(project => project.status === PENDING_RRE_REVIEW);
        break;
      case QUICK_FILTER.IN_PROGRESS:
        results = this.projects.filter(project => project.status === IN_PROGRESS);
        break;
      case 'unassigned':
        results = this.projects.filter(project => !project.assignedTo && project.status !== 'Deleted');
        break;
      case 'completed':
        results = this.projects.filter(project => project.status === COMPLETED);
        break;
      default:
        results = this.projects.filter(project => project.status !== 'Deleted');
    }

    const search = this.dashboardSearchText.trim().toLowerCase();
    if (search) {
      results = results.filter(project =>
        project.name.toLowerCase().includes(search) ||
        project.collection.toLowerCase().includes(search) ||
        project.subCollections.some(subCollection => subCollection.toLowerCase().includes(search)) ||
        project.contactPerson.toLowerCase().includes(search) ||
        (project.assignedTo || '').toLowerCase().includes(search) ||
        project.status.toLowerCase().includes(search)
      );
    }

    results = results.slice().sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return dateB - dateA;
    });

    return results;
  }

  get pendingProjects(): ProjectRecord[] {
    return this.filteredProjects.filter(p => p.status !== COMPLETED && p.status !== PENDING_RAES_REVIEW);
  }

  get completedOrReviewProjects(): ProjectRecord[] {
    return this.filteredProjects.filter(p => p.status === COMPLETED || p.status === PENDING_RAES_REVIEW);
  }

  get pendingManagerApprovals(): ProjectRecord[] {
    return this.projects.filter(project => project.status === PENDING_RRE_REVIEW);
  }

  get pendingAdminReview(): ProjectRecord[] {
    return this.projects.filter(project => project.status === PENDING_RAES_REVIEW);
  }

  get assignedProjectsCount(): number {
    return this.projects.filter(project => !!project.assignedTo).length;
  }

  get unassignedProjectsCount(): number {
    return this.projects.filter(project => !project.assignedTo).length;
  }

  get overdueProjectsCount(): number {
    return this.projects.filter(project => this.isOverdue(project)).length;
  }

  setActiveTab(tab: UserTab): void {
    if (this.isTabDisabled(tab)) {
      return;
    }
    this.activeTab = tab;
    if (tab === USER_TAB.MANAGE_APPROVAL) {
      this.router.navigate(['/user-dashboard/manage-approval']);
    } else {
      this.router.navigate(['/user-dashboard']);
    }
    if (tab === USER_TAB.MANAGE_PROJECT) {
      setTimeout(() => {
        this.initializePaginator();
        this.updatePagination();
      });
    }
  }

  isTabDisabled(tab: UserTab): boolean {
    if (tab === USER_TAB.MANAGE_APPROVAL) {
      return this.assignedProjectsCount === 0;
    }

    return false;
  }

  toggleSideNav(): void {
    this.isSideNavCollapsed = !this.isSideNavCollapsed;
  }

  getTabIcon(tab: UserTab): string {
    switch (tab) {
      case USER_TAB.DASHBOARD:
        return 'dashboard';
      case USER_TAB.MANAGE_APPROVAL:
        return 'fact_check';
      case USER_TAB.MANAGE_PROJECT:
        return 'folder_open';
      case USER_TAB.HELP_SUPPORT:
        return 'help_outline';
      default:
        return 'menu';
    }
  }

  setQuickFilter(filter: QuickFilter): void {
    this.quickFilter = filter;
  }

  viewProject(projectId: number, departmentId = ''): void {
    // fromManageApproval is explicitly set to 'false' here (rather than omitted)
    // so this plain dashboard view is unaffected by the project-detail page's
    // default-to-true behavior for links that omit the param entirely (e.g. the
    // RRE assignment email link). departmentId is built first so the resulting
    // URL matches the backend's own link format: ?departmentId=...&fromManageApproval=...
    const queryParams: Record<string, string> = {};
     if (departmentId) {
      this.router.navigate(['/user-dashboard/project', projectId, { departmentId: departmentId }]);
    } else {
      this.router.navigate(['/user-dashboard/project', projectId]);
    }
  }

  managerApprove(project: ProjectRecord): void {
    project.status = PENDING_RAES_REVIEW;
  }

  adminMarkCompleted(project: ProjectRecord): void {
    project.status = COMPLETED;
  }

  getStatusBadgeClass(status: ProjectStatus): string {
    // Normalize status to handle different case formats
    const normalizedStatus = String(status)
      .toLowerCase()
      .trim()
      .replaceAll(/\s+/g, ' ');

    switch (normalizedStatus) {
      case 'completed':
        return 'badge-success';
      case 'submitted':
      case 'in progress':
        return 'badge-progress';
      case 'pending rre review':
      case 'pending raes review':
        return 'badge-danger';
      case 'assigned':
        return 'badge-assigned';
      case 'unassigned':
        return 'badge-unassigned';
      case 'deleted':
        return 'badge-deleted';
      case 'overdue':
        return 'badge-overdue';
      default:
        return 'badge-secondary';
    }
  }

  getDisplayStatus(project: any): string {
    return project.status || project.sub_task_status || 'Unknown';
  }

  getDisplayStatusClass(project: any): string {
    const status = project.status || project.sub_task_status || '';
    return this.getStatusBadgeClass(status);
  }

  getAssignmentLabel(project: ProjectRecord): string {
    return project.assignedTo ? 'Assigned To' : 'Assignment';
  }

  getAssignedName(project: ProjectRecord): string {
    if (!project.assignedTo) {
      return UNASSIGNED;
    }

    return project.assignedTo ? project.assignedTo : 'Unknown';
  }

  getManagerName(project: ProjectRecord): string {
    if (!project.managerId) {
      return 'Not assigned';
    }

    const manager = this.departmentMembers.find(item => item.id === project.managerId);
    return manager ? manager.name : 'Unknown';
  }

  getDueProgress(project: ProjectRecord): number {
    const daysRemaining = this.getDaysRemaining(project.dueDate);

    if (daysRemaining <= 0) {
      return 100;
    }

    // Use the larger of 30 or actual days remaining as the total window
    const totalWindow = Math.max(30, daysRemaining);
    const progress = ((totalWindow - daysRemaining) / totalWindow) * 100;
    // Always show at least 8% so the bar is visible
    return Math.max(8, Math.min(100, Math.round(progress)));
  }

  getDueState(project: ProjectRecord): DueState {
    const daysRemaining = this.getDaysRemaining(project.dueDate);

    if (daysRemaining < 1) {
      return 'overdue';
    }

    if (daysRemaining <= 4) {
      return 'near';
    }

    if (daysRemaining <= 8) {
      return 'warning';
    }

    return 'normal';
  }

  getDueLabel(project: ProjectRecord): string {
    const daysRemaining = this.getDaysRemaining(project.dueDate);

    if (Number.isNaN(daysRemaining)) {return ''};

    if (daysRemaining < 0) {
      const overdueDays = Math.abs(daysRemaining);
      return `Overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}`;
    }

    if (daysRemaining === 0) {
      return 'Due today';
    }

    return `Due in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
  }

  private isOverdue(project: ProjectRecord): boolean {
    return this.getDaysRemaining(project.dueDate) < 0 && project.status !== COMPLETED;
  }

  private parseDateLocal(dateStr: string): Date | null {
    if (!dateStr) {return null};
    const datePart = dateStr.split('T')[0].split(' ')[0];
    if (datePart.includes('-')) {
      const [year, month, day] = datePart.split('-').map(Number);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        return new Date(year, month - 1, day);
      }
    } else if (datePart.includes('/')) {
      const [month, day, year] = datePart.split('/').map(Number);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        return new Date(year, month - 1, day);
      }
    }
    return null;
  }

  private getDaysRemaining(dueDate: string): number {
    const dueDateOnly = this.parseDateLocal(dueDate);
    if (!dueDateOnly || Number.isNaN(dueDateOnly.getTime())){ return Number.NaN};
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((dueDateOnly.getTime() - todayOnly.getTime()) / millisecondsPerDay);
  }

  formatDate(dateStr: string, format: 'short' | 'long' = 'long'): string {
    if (!dateStr) {return 'N/A'};
    const d = this.parseDateLocal(dateStr);
    if (!d || Number.isNaN(d.getTime())) {return dateStr};
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (format === 'short') {return `${mm}/${dd}/${yyyy}`};
    return `${months[d.getMonth()]} ${dd}, ${yyyy}`;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.searchText = '';
    this.dataSource.filter = '';
  }

  applyStatusFilter(status: string): void {
    this.selectedStatusFilter = status;
    this.applyAllFilters();
  }

  applyDateFilter(days: string): void {
    this.selectedDateFilter = days;
    this.applyAllFilters();
  }

  private formatDateForSearch(dateStr: string): string[] {
    if (!dateStr) {return []};
    const d = this.parseDateLocal(dateStr);
    if (!d || Number.isNaN(d.getTime())) {return []};
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return [`${mm}/${dd}/${yyyy}`]; // 06/15/2025
  }

  applyAllFilters(): void {
    this.dataSource.filterPredicate = (data: any, _filter: string) => {
      const search = this.searchText.toLowerCase();
      const dateMatches = (dateStr: string) =>
        this.formatDateForSearch(dateStr).some(fmt => fmt.includes(search));

      const searchMatch = !this.searchText || 
        (data.project_name?.toLowerCase().includes(search) ||
         data.contact_person?.toLowerCase().includes(search) ||
         data.collection_name?.toLowerCase().includes(search) ||
         dateMatches(data.due_date) ||
         dateMatches(data.updated_at) ||
         (data.sub_task_status || data.status || '').toLowerCase().includes(search));

      let statusMatch = true;
      if (this.selectedStatusFilter !== 'all') {
        const rawStatus = data.sub_task_status || data.status || '';
        const projectStatus = rawStatus.toLowerCase().trim();
        
        const statusMap: { [key: string]: string[] } = {
          'unassigned': ['unassigned'],
          [QUICK_FILTER.IN_PROGRESS]: ['in progress'],
          'pending-rre-review': ['pending rre review'],
          'pending-raes-review': ['pending raes review'],
          'completed': ['completed'],
          'overdue': ['overdue']
        };
        const allowedStatuses = statusMap[this.selectedStatusFilter] || [];
        statusMatch = allowedStatuses.some(s => projectStatus.includes(s));
      }

      let dateMatch = true;
      if (this.selectedDateFilter !== '0') {
        const days = Number.parseInt(this.selectedDateFilter, 10);
        const createdDate = new Date(data.updated_at || data.created_at);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        dateMatch = createdDate >= cutoffDate;
      }

      return searchMatch && statusMatch && dateMatch;
    };

    this.dataSource.filter = Date.now().toString();
    
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    this.updatePagination();
  }

  getSubCollection(subCollection: string[]): string {
    if (!subCollection || subCollection.length === 0) {return 'N/A'};
    return subCollection.join(', ');
  }
}
